import { useState, useCallback } from 'react';
import { reviewsApi } from '../api/reviews';
import type { ProductReview, ReviewStatus } from '../api/reviews';

export function useReviews() {
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>('pending');

  const fetchReviews = useCallback(async (p = 1, status?: ReviewStatus) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reviewsApi.list(p, status);
      setReviews(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load reviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  const moderate = useCallback(async (id: number, status: 'approved' | 'rejected') => {
    try {
      await reviewsApi.updateStatus(id, status);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update review.');
    }
  }, []);

  const reply = useCallback(async (id: number, replyText: string) => {
    try {
      const res = await reviewsApi.reply(id, replyText);
      setReviews((prev) => prev.map((r) => (r.id === id ? res.data.data : r)));
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to post reply.');
      return null;
    }
  }, []);

  const deleteReview = useCallback(async (id: number) => {
    try {
      await reviewsApi.destroy(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete review.');
    }
  }, []);

  return {
    reviews, loading, error, total, page, lastPage, statusFilter, setStatusFilter,
    fetchReviews, moderate, reply, deleteReview, setError,
  };
}
